// Shared vocabulary for the cases queue: which tab a case belongs to, and when
// a case counts as overdue. Kept in one file so the sidebar badge, the queue
// tabs and the satisfaction strip can never disagree about a case.

// Anything not resolved or closed is still on someone's desk.
export const CASE_OPEN_STATUSES = [
  'new',
  'open',
  'in_progress',
  'waiting_customer',
  'pending_internal',
];

export function isCaseOpen(c) {
  return !!c && CASE_OPEN_STATUSES.includes(c.status);
}

// A customer is waiting on us when their newest inbound message is later than
// our first reply — or when we have never replied at all. Past the threshold
// the case is overdue.
export const CASE_OVERDUE_HOURS = 24;

export function isAwaitingReply(c) {
  if (!c?.last_inbound_at) return false;
  if (!c.first_response_at) return true;
  return new Date(c.last_inbound_at).getTime() > new Date(c.first_response_at).getTime();
}

export function isCaseOverdue(c, now = Date.now()) {
  if (!isCaseOpen(c) || !isAwaitingReply(c)) return false;
  return now - new Date(c.last_inbound_at).getTime() > CASE_OVERDUE_HOURS * 3600000;
}

// A case the customer rated 1–3 after resolution, which put it back in the
// queue for a supervisor.
export function isFeedbackReopened(c) {
  const rating = Number(c?.feedback_rating);
  return rating >= 1 && rating <= 3 && isCaseOpen(c);
}

export const CASE_TABS = [
  { id: 'new', label: 'New', match: (c) => c.status === 'new' },
  {
    id: 'open',
    label: 'Open',
    match: (c) => ['open', 'in_progress', 'pending_internal'].includes(c.status),
  },
  { id: 'waiting', label: 'Waiting Customer', match: (c) => c.status === 'waiting_customer' },
  { id: 'feedback', label: 'Reopened Feedback', match: isFeedbackReopened },
  { id: 'closed', label: 'Resolved / Closed', match: (c) => ['resolved', 'closed'].includes(c.status) },
];

export function matchesTab(c, tabId) {
  const tab = CASE_TABS.find((t) => t.id === tabId);
  return tab ? tab.match(c) : true;
}

// Satisfaction across every survey a customer has answered.
export function satisfactionSummary(cases) {
  const rated = (cases || []).filter((c) => Number(c.feedback_rating) >= 1);
  const sum = rated.reduce((total, c) => total + Number(c.feedback_rating), 0);
  return {
    total: rated.length,
    average: rated.length ? sum / rated.length : 0,
    low: rated.filter((c) => Number(c.feedback_rating) <= 3).length,
  };
}